import { useZero } from "@rocicorp/zero/react";
import { useCallback, useState } from "react";
import { sharedMutators } from "../../../../shared/mutators";
import { formatDate } from "../../../lib/formatTimestamp";
import { Box } from "../../Box";
import { Button } from "../../Button";
import { ConfirmModal } from "../../ConfirmModal";
import { Text } from "../../Text";

const DAY_MS = 1000 * 60 * 60 * 24;

function expiryLabel(expiresAt: number): {
  text: string;
  color: string;
} {
  const days = Math.ceil((expiresAt - Date.now()) / DAY_MS);
  if (days <= 0)
    return {
      text: `Token expired on ${formatDate(expiresAt)}. Revoke it and re-enable SCIM to mint a new one.`,
      color: "var(--text-danger)",
    };
  const remaining = days === 1 ? "1 day" : `${days} days`;
  return {
    text: `Token expires on ${formatDate(expiresAt)} (in ${remaining}).`,
    color: days <= 30 ? "var(--text-danger)" : "var(--text-secondary)",
  };
}

export function ScimTokenSection({
  providerId,
  expiresAt,
  onRevoked,
}: {
  providerId: string;
  expiresAt?: number | null;
  onRevoked: () => void;
}) {
  const z = useZero();
  const [confirmRevoke, setConfirmRevoke] = useState(false);

  const handleRevokeToken = useCallback(() => {
    z.mutate(sharedMutators.revokeScimToken({ ssoProviderId: providerId }));
    setConfirmRevoke(false);
    onRevoked();
  }, [z, providerId, onRevoked]);

  const expiry = expiresAt != null ? expiryLabel(expiresAt) : null;

  return (
    <Box horizontal={8} top={8}>
      <Text variant="text-small" color="var(--text-secondary)">
        Your IdP authenticates with a bearer token. Revoking it sets sync mode
        back to off.
      </Text>
      {expiry && (
        <Box top={4}>
          <Text variant="text-small-md" color={expiry.color}>
            {expiry.text}
          </Text>
        </Box>
      )}
      <Box top={8}>
        <div>
          <Button
            type="button"
            variant="secondary"
            size="m"
            onClick={() => setConfirmRevoke(true)}
          >
            Revoke token
          </Button>
        </div>
      </Box>

      <ConfirmModal
        open={confirmRevoke}
        onClose={() => setConfirmRevoke(false)}
        onConfirm={handleRevokeToken}
        title="Revoke SCIM token"
        description="The existing SCIM token will be invalidated and IdP sync will be disabled. To re-enable SCIM, select it as the sync mode again and a new token will be generated."
        confirmLabel="Revoke"
        destructive
      />
    </Box>
  );
}
